import React, { useRef, useEffect, useState } from 'react';
import { BookOpen, Globe, ArrowRight, Briefcase, GraduationCap, Cpu, FlaskConical, Palette, Stethoscope, Scale, Building2, ChevronLeft, ChevronRight } from 'lucide-react';
import getFullUrl from '../../utils/formatUrl';

// Map of icon names to components for dynamic rendering
const iconMap = {
    BookOpen, Globe, Briefcase, GraduationCap, Cpu, FlaskConical, Palette, Stethoscope, Scale, Building2
};

const defaultCourses = [
    { title: 'Technical Education', desc: 'B.Tech programs in 12+ specializations with research focus.', icon: 'BookOpen' },
    { title: 'Management & Business', desc: 'MBA and BBA programs with niche specializations.', icon: 'Briefcase' },
    { title: 'Computer Applications', desc: 'Mastering the world of software with MCA and BCA.', icon: 'Globe' },
];

const CourseSections = ({ data = {}, courses = [], onInquiry }) => {
    const scrollRef = useRef(null);
    const [isPaused, setIsPaused] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeftState, setScrollLeftState] = useState(0);
    const [direction, setDirection] = useState(1); // 1 for right, -1 for left

    const handleManualScroll = (offset) => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: offset, behavior: 'smooth' });
            setDirection(offset > 0 ? 1 : -1);
        }
    };

    const displayCourses = courses.length > 0 ? courses : defaultCourses;

    // Duplicate courses for smooth marquee effect
    const marqueeItems = [...displayCourses, ...displayCourses, ...displayCourses, ...displayCourses];

    useEffect(() => {
        const scrollContainer = scrollRef.current;
        if (!scrollContainer) return;

        let animationFrameId;
        const scrollSpeed = 0.8;

        const autoScroll = () => {
            if (!isPaused && !isDragging) {
                scrollContainer.scrollLeft += (scrollSpeed * direction);

                // Seamless wrap-around logic
                const threshold = scrollContainer.scrollWidth / 4;
                if (direction === 1 && scrollContainer.scrollLeft >= threshold * 3) {
                    scrollContainer.scrollLeft = threshold;
                } else if (direction === -1 && scrollContainer.scrollLeft <= threshold) {
                    scrollContainer.scrollLeft = threshold * 3;
                }
            }
            animationFrameId = requestAnimationFrame(autoScroll);
        };

        animationFrameId = requestAnimationFrame(autoScroll);
        return () => cancelAnimationFrame(animationFrameId);
    }, [isPaused, isDragging, direction]);

    // ── Mouse handlers ──
    const handleMouseDown = (e) => {
        setIsDragging(true);
        setIsPaused(true);
        setStartX(e.pageX - scrollRef.current.offsetLeft);
        setScrollLeftState(scrollRef.current.scrollLeft);
    };
    const handleMouseLeave = () => {
        setIsDragging(false);
        setIsPaused(false);
    };
    const handleMouseUp = () => {
        setIsDragging(false);
        setTimeout(() => setIsPaused(false), 500);
    };
    const handleMouseMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - scrollRef.current.offsetLeft;
        const walk = (x - startX) * 2;
        const prevScroll = scrollRef.current.scrollLeft;
        scrollRef.current.scrollLeft = scrollLeftState - walk;
        if (scrollRef.current.scrollLeft > prevScroll) setDirection(1);
        else if (scrollRef.current.scrollLeft < prevScroll) setDirection(-1);
    };

    // ── Touch handlers ──
    const handleTouchStart = (e) => {
        setIsDragging(true);
        setIsPaused(true);
        setStartX(e.touches[0].pageX - scrollRef.current.offsetLeft);
        setScrollLeftState(scrollRef.current.scrollLeft);
    };
    const handleTouchMove = (e) => {
        if (!isDragging) return;
        // Allow horizontal scroll, prevent page scroll
        const x = e.touches[0].pageX - scrollRef.current.offsetLeft;
        const walk = (x - startX) * 1.5;
        const prevScroll = scrollRef.current.scrollLeft;
        scrollRef.current.scrollLeft = scrollLeftState - walk;
        if (scrollRef.current.scrollLeft > prevScroll) setDirection(1);
        else if (scrollRef.current.scrollLeft < prevScroll) setDirection(-1);
    };
    const handleTouchEnd = () => {
        setIsDragging(false);
        // Resume auto-scroll after a short delay
        setTimeout(() => setIsPaused(false), 800);
    };

    return (
        <section className="py-10 bg-slate-900 text-white relative overflow-hidden select-none">
            <div className="absolute top-0 right-0 w-1/3 h-full bg-[#cc0000]/5 -skew-x-12"></div>
            <div className="max-w-7xl mx-auto px-4 relative z-10 mb-8">
                <div className="flex flex-col md:flex-row justify-between items-end">
                    <div className="max-w-xl">
                        <h3 className="text-[#cc0000] font-black uppercase tracking-[5px] text-[11px] mb-4">{data.title || 'Academic Programs'}</h3>
                        <h2 className="text-4xl md:text-5xl font-black uppercase tracking-tighter">Choose Your <span className="text-[#cc0000]">{data.highlight || 'Future'}</span> Path.</h2>
                    </div>
                </div>
            </div>

            <div
                ref={scrollRef}
                className={`relative flex overflow-x-hidden group cursor-${isDragging ? 'grabbing' : 'grab'} py-8`}
                style={{ touchAction: 'pan-x' }}
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseLeave}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
                onMouseEnter={() => setIsPaused(true)}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                <div className="flex">
                    {marqueeItems.map((c, i) => {
                        const IconComponent = iconMap[c.icon] || BookOpen;
                        return (
                            <div
                                key={i}
                                onClick={() => onInquiry({ title: c.title, colleges: c.colleges || (c.college ? [c.college] : []) })}
                                className="flex-shrink-0 w-[140px] md:w-[400px] mx-1 md:mx-4 p-4 md:p-12 border border-white/10 transition-all duration-500 group/card cursor-pointer relative overflow-hidden rounded-sm"
                                style={c.bgImage ? {
                                    backgroundImage: `url("${getFullUrl(c.bgImage)}")`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    backgroundRepeat: 'no-repeat'
                                } : {}}
                            >
                                {/* Overlay System */}
                                <div className={`absolute inset-0 transition-all duration-500 ${
                                    c.bgImage
                                        ? 'bg-slate-900/70 group-hover/card:bg-slate-900/40'
                                        : 'bg-white/5 group-hover/card:bg-white'
                                }`}></div>

                                <div className="relative z-10 pointer-events-none">
                                    <div className="h-10 md:h-20 mb-4 md:mb-8 flex items-center">
                                        {c.logo ? (
                                            <img
                                                src={getFullUrl(c.logo)}
                                                className={`h-full w-auto object-contain transition-all ${c.bgImage ? 'brightness-110 drop-shadow-xl' : ''}`}
                                                alt={c.title}
                                            />
                                        ) : (
                                            <IconComponent size={24} className="md:w-12 md:h-12 text-[#cc0000] group-hover/card:scale-110 transition-transform" />
                                        )}
                                    </div>
                                    <h4 className={`text-[10px] md:text-xl font-black uppercase mb-1 md:mb-4 transition-colors leading-tight ${
                                        c.bgImage ? 'text-white' : 'group-hover/card:text-slate-900'
                                    }`}>{c.title === 'LAW COLLAGE' ? 'LAW COLLEGE' : c.title}</h4>
                                    <p className={`text-[8px] md:text-sm leading-tight md:leading-relaxed mb-4 md:mb-8 h-8 md:h-20 overflow-hidden line-clamp-2 md:line-clamp-3 transition-colors ${
                                        c.bgImage ? 'text-slate-300 group-hover/card:text-white' : 'text-slate-400 group-hover/card:text-slate-600'
                                    }`}>
                                        {c.desc}
                                    </p>
                                    <button className={`flex items-center font-black text-[7px] md:text-[10px] tracking-widest uppercase transition-colors ${
                                        c.bgImage ? 'text-white group-hover:text-white' : 'text-[#cc0000]'
                                    }`}>
                                        ENROLL <span className="hidden md:inline">NOW</span> <ArrowRight size={10} className="md:w-4 md:h-4 ml-1 md:ml-2" />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Mobile Navigation Buttons */}
                <div className="md:hidden absolute left-2 top-1/2 -translate-y-1/2 z-20 flex gap-2 w-full justify-between px-2 pointer-events-none">
                    <button
                        onClick={() => handleManualScroll(-160)}
                        className="w-8 h-8 rounded-full bg-slate-900/90 shadow-lg border border-white/20 flex items-center justify-center text-[#cc0000] pointer-events-auto active:scale-95 transition-transform"
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <button
                        onClick={() => handleManualScroll(160)}
                        className="w-8 h-8 rounded-full bg-slate-900/90 shadow-lg border border-white/20 flex items-center justify-center text-[#cc0000] pointer-events-auto active:scale-95 transition-transform"
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>
            </div>
        </section>
    );
};

export default CourseSections;
